import { useState, useRef, useEffect, useMemo } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { GroupService } from '@cougargrades/services'
import type { LiteGroupResult, SearchResult } from '@cougargrades/models/dto'
import { computeUDI, deriveUDIInputs } from '@cougargrades/utils'
import type { UDIResult } from '@cougargrades/utils'
import { API_ORIGIN } from '@cougargrades/services/environment'
import { useStats } from '../lib/services/useStats'
import { useTrending } from '../lib/services/useTrending'
import { cleanGroupName } from '../lib/util'
import { getSchoolTheme } from '../lib/schoolThemes'
import { UDIBadge } from '../components/ui/UDIBadge'
import SearchBar from '../components/search'

// Shape returned by /api/course/:courseName (subset we need)
interface CourseSpotlightApiShape {
  badges: Array<{ key: string; text: string; color: string }>
  dataGrid: { rows: Array<{ A: number; B: number; C: number; D: number; F: number; W: number }> }
  sectionCount: number
}

interface CourseEnrichmentData {
  avgGPA: number
  gradeCounts: { A: number; B: number; C: number; D: number; F: number; W: number }
  sectionCount: number
}

// Same fetch pattern as search.tsx and GpaPredictor.tsx — uses /api/course/:id (full endpoint)
async function fetchCourseEnrichments(ids: string[]): Promise<Record<string, CourseEnrichmentData | null>> {
  const results = await Promise.all(
    ids.map(async id => {
      try {
        const res = await fetch(`${API_ORIGIN.origin}/api/course/${encodeURIComponent(id)}`)
        if (!res.ok) return null
        const json = await res.json() as CourseSpotlightApiShape
        const avgGPA = parseFloat(json.badges?.find(b => b.key === 'gpa')?.text ?? '0') || 0
        const gradeCounts = { A: 0, B: 0, C: 0, D: 0, F: 0, W: 0 }
        for (const row of (json.dataGrid?.rows ?? [])) {
          gradeCounts.A += row.A ?? 0
          gradeCounts.B += row.B ?? 0
          gradeCounts.C += row.C ?? 0
          gradeCounts.D += row.D ?? 0
          gradeCounts.F += row.F ?? 0
          gradeCounts.W += row.W ?? 0
        }
        return { avgGPA, gradeCounts, sectionCount: json.sectionCount ?? 0 }
      } catch {
        return null
      }
    })
  )
  const map: Record<string, CourseEnrichmentData | null> = {}
  ids.forEach((id, i) => { map[id] = results[i] })
  return map
}

// ─── Animation styles ─────────────────────────────────────────────────────────

const PAGE_STYLES = `
@keyframes bounce-y { 0%,100% { transform: translateY(0) } 50% { transform: translateY(8px) } }
@keyframes pulse-dot { 0%,100% { opacity: 1 } 50% { opacity: 0.3 } }
@keyframes spin { to { transform: rotate(360deg) } }
.bounce-arrow { animation: bounce-y 1.4s ease-in-out infinite }
.pulse-dot { animation: pulse-dot 1.5s ease-in-out infinite }
.udi-fade-enter { opacity: 0; transform: translateY(10px); transition: opacity 0.3s ease, transform 0.3s ease }
.udi-fade-visible { opacity: 1; transform: translateY(0) }

/* ── Mobile responsive overrides (≤ 640px) ── */
@media (max-width: 640px) {
  .courses-hero-badge { display: none !important; }
  .courses-section-header { flex-wrap: wrap !important; }
}`

// ─── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/courses')({
  head: () => ({
    meta: [
      { title: 'Browse Courses | Unilytics' },
      {
        name: 'description',
        content:
          'Browse courses and departments at the University of Houston. Compare difficulty, GPA, and find the professors who help students succeed.',
      },
    ],
  }),
  component: CoursesPage,
})

// ─── Data hook ────────────────────────────────────────────────────────────────

function useGroupsForLanding() {
  return useQuery<LiteGroupResult[]>({
    queryKey: ['all-groups'],
    queryFn: async () => {
      const svc = new GroupService()
      const data = await svc.GetAllGroups()
      return data ?? []
    },
  })
}

// ─── Constants ────────────────────────────────────────────────────────────────

const COLLEGE_QUICK_FILTERS = [
  'All', 'Engineering', 'Business', 'Liberal Arts', 'Natural Sciences',
  'Architecture', 'Education', 'Nursing', 'Law', 'Hospitality', 'Honors',
]

// Mapping of lowercase keyword → emoji for college cards
const COLLEGE_EMOJI_MAP: Array<[string, string]> = [
  ['engineering', '⚙️'],
  ['business', '💼'],
  ['liberal arts', '📚'],
  ['natural sciences', '🔬'],
  ['architecture', '🏛️'],
  ['education', '🎓'],
  ['nursing', '🏥'],
  ['law', '⚖️'],
  ['pharmacy', '💊'],
  ['technology', '💻'],
  ['hospitality', '🍽️'],
  ['optometry', '👁️'],
  ['social work', '🤝'],
  ['art', '🎨'],
  ['public affairs', '🏢'],
  ['honors', '⭐'],
  ['graduate', '📜'],
]

// ─── Helpers ─────────────────────────────────────────────────────────────────

function nonHashCategory(categories: string[]): string {
  return categories.filter(c => !c.startsWith('#'))[0] ?? 'Other Groups'
}

function extractGpa(badges: SearchResult['badges']): number | null {
  const text = badges.find(b => b.key === 'gpa')?.text ?? ''
  const n = parseFloat(text)
  return n > 0 ? n : null
}

function extractDropRate(badges: SearchResult['badges']): number {
  const text = badges.find(b => b.key === 'droprate')?.text ?? ''
  return parseFloat(text) || 0
}

function getCollegeEmoji(name: string): string {
  const lower = name.toLowerCase()
  for (const [keyword, emoji] of COLLEGE_EMOJI_MAP) {
    if (lower.includes(keyword)) return emoji
  }
  return '🏫'
}

function syntheticInsight(gpa: number | null, dropRate: number): string {
  if (dropRate >= 20) return `${dropRate.toFixed(0)}% of students withdraw — have a backup plan.`
  if (dropRate >= 10) return `${dropRate.toFixed(0)}% withdrawal rate signals serious course difficulty.`
  if (gpa !== null && gpa < 2.0) return `Avg GPA of ${gpa.toFixed(2)} — among the hardest courses at UH.`
  if (gpa !== null && gpa < 2.5) return `Avg GPA ${gpa.toFixed(2)} with ${dropRate.toFixed(0)}% W rate — plan ahead.`
  if (dropRate > 5) return `${dropRate.toFixed(1)}% of students withdraw from this course.`
  return `High-demand course — check professor reviews before enrolling.`
}

// ─── Main component ───────────────────────────────────────────────────────────

function CoursesPage() {
  const theme = getSchoolTheme('uh')
  const { data: groups, isPending } = useGroupsForLanding()
  const { data: stats } = useStats()
  const { data: trending } = useTrending(20)
  const [activeFilter, setActiveFilter] = useState('All')
  const [showAllColleges, setShowAllColleges] = useState(false)
  const gridRef = useRef<HTMLDivElement>(null)

  // Groups API returns: identifier, name, categories, href, courseCount, sectionCount, relatedGroups
  // No per-group GPA or UDI data — those require per-course data from /api/course/:id
  const collegeCategoryGroups = useMemo(
    () => (groups ?? []).filter(g => nonHashCategory(g.categories) === 'Colleges/Schools'),
    [groups],
  )

  const maxCourseCount = useMemo(
    () => collegeCategoryGroups.reduce((max, g) => Math.max(max, g.courseCount), 1),
    [collegeCategoryGroups],
  )

  const filteredColleges = useMemo(() => {
    return collegeCategoryGroups.filter(g => {
      const name = cleanGroupName(g.name).toLowerCase()
      return activeFilter === 'All' || name.includes(activeFilter.toLowerCase())
    })
  }, [collegeCategoryGroups, activeFilter])

  // Show 6 by default when "All" filter active; show all when expanded or filtered
  const displayedColleges = activeFilter !== 'All' || showAllColleges
    ? filteredColleges
    : filteredColleges.slice(0, 6)
  const hasMore = activeFilter === 'All' && filteredColleges.length > 6

  // Enrich trending courses with full grade data for accurate UDI computation
  const trendingCourseIds = useMemo(
    () => (trending ?? []).filter(r => r.type === 'course' && r.href.startsWith('/c/')).map(r => decodeURIComponent(r.href.slice(3))),
    [trending],
  )
  const { data: trendingEnrichments } = useQuery({
    queryKey: ['course-enrichment-courses-page', trendingCourseIds],
    queryFn: () => fetchCourseEnrichments(trendingCourseIds),
    enabled: trendingCourseIds.length > 0,
    staleTime: 1000 * 60 * 5,
  })

  // Risk section: sort trending courses by W rate desc, take top 3
  // UDI computed from full enriched data once available, otherwise omit badge
  const riskCourses = useMemo(() => {
    return (trending ?? [])
      .filter(r => r.type === 'course')
      .map(r => {
        const gpa = extractGpa(r.badges)
        const dropRate = extractDropRate(r.badges)
        const courseId = decodeURIComponent(r.href.slice(3))
        const enrichment = trendingEnrichments?.[courseId]
        const udi = enrichment
          ? computeUDI(deriveUDIInputs({ avgGPA: enrichment.avgGPA, grades: enrichment.gradeCounts, sections: enrichment.sectionCount, schoolId: 'uh' })).udi
          : null
        return { ...r, gpa, dropRate, udi }
      })
      .sort((a, b) => b.dropRate - a.dropRate)
      .slice(0, 3)
  }, [trending, trendingEnrichments])

  // Trending courses for horizontal scroll — all courses from trending
  const trendingCourses = useMemo(
    () => (trending ?? []).filter(r => r.type === 'course').map(r => ({
      ...r,
      gpa: extractGpa(r.badges),
      dropRate: extractDropRate(r.badges),
    })),
    [trending],
  )

  const courseCount = stats?.totalCourses ?? 7600
  const profCount = stats?.totalProfessors ?? 6200
  const deptCount = collegeCategoryGroups.length || 28

  return (
    <div style={{ background: '#F8FAFC', minHeight: '100vh', fontFamily: 'var(--font-family-sans)' }}>
      <style>{PAGE_STYLES}</style>

      {/* ── HERO ──────────────────────────────────────────────────────── */}
      <section
        style={{
          minHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '80px 24px 60px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Colour gradient base layer */}
        <div
          aria-hidden
          style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: `linear-gradient(160deg, ${theme.primaryColor} 0%, color-mix(in srgb, ${theme.primaryColor} 65%, #000) 100%)`,
            zIndex: 0,
          }}
        />
        {/* Grid pattern overlay */}
        <div
          aria-hidden
          style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            backgroundImage:
              'linear-gradient(rgba(0,0,0,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.15) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
            zIndex: 1,
          }}
        />

        {/* UH badge top-left */}
        <div className="courses-hero-badge" style={{ position: 'absolute', top: 24, left: 24, display: 'flex', alignItems: 'center', gap: 10, zIndex: 2 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: theme.secondaryColor,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 900, fontSize: 12, color: theme.primaryColor,
            fontFamily: 'var(--font-family-mono)', flexShrink: 0,
          }}>
            UH
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.9)', lineHeight: 1.3 }}>
              University of Houston
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
              <span className="pulse-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ADE80', display: 'inline-block' }} />
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>Live Data</span>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div style={{ textAlign: 'center', maxWidth: 680, position: 'relative', zIndex: 2 }}>
          <p style={{ fontSize: 13, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.65)', marginBottom: 20, fontWeight: 600 }}>
            University of Houston · {courseCount.toLocaleString()}+ Courses
          </p>
          <h1 style={{ fontSize: 'clamp(40px, 7vw, 68px)', fontWeight: 900, color: '#fff', lineHeight: 1.08, letterSpacing: '-0.03em', marginBottom: 20 }}>
            Find your courses<span style={{ color: theme.secondaryColor }}>.</span>
          </h1>
          <p style={{ fontSize: 'clamp(15px, 2vw, 18px)', color: 'rgba(255,255,255,0.82)', maxWidth: 520, margin: '0 auto 32px', lineHeight: 1.65 }}>
            Browse by major, compare difficulty, and find the professors who help you succeed.
          </p>

          {/* Search bar — typeahead */}
          <div style={{ maxWidth: 560, margin: '0 auto 24px' }}>
            <SearchBar />
          </div>

          {/* Stat chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
            {[
              { value: courseCount, suffix: '+', label: 'courses' },
              { value: profCount, suffix: '+', label: 'professors' },
              { value: deptCount, suffix: '', label: 'colleges & schools' },
            ].map(({ value, suffix, label }) => (
              <div key={label} style={{
                background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)',
                borderRadius: 999, padding: '7px 18px',
                fontSize: 13, color: 'rgba(255,255,255,0.75)',
                fontFamily: 'var(--font-family-mono)',
              }}>
                {value.toLocaleString()}{suffix} {label}
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <button
          onClick={() => gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          style={{
            position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)',
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, zIndex: 2,
          }}
        >
          <span className="bounce-arrow" style={{ fontSize: 22, color: 'rgba(255,255,255,0.5)' }}>↓</span>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Explore departments
          </span>
        </button>
      </section>

      {/* ── FILTER CHIPS ──────────────────────────────────────────────── */}
      <div style={{
        background: '#fff', borderBottom: '1px solid rgba(13,27,42,0.07)',
        padding: '16px 0', position: 'sticky', top: 0, zIndex: 10,
      }}>
        <div className="new-container">
          <div className="flex items-center gap-2" style={{ overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: 2 }}>
            {COLLEGE_QUICK_FILTERS.map(filter => {
              const isActive = activeFilter === filter
              return (
                <button
                  key={filter}
                  onClick={() => { setActiveFilter(filter); setShowAllColleges(false) }}
                  style={{
                    whiteSpace: 'nowrap', padding: '7px 16px', borderRadius: 999,
                    border: `1.5px solid ${isActive ? 'var(--school-primary)' : 'rgba(var(--school-primary-rgb),0.3)'}`,
                    background: isActive ? 'var(--school-primary)' : '#fff',
                    color: isActive ? '#fff' : 'var(--school-primary)',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    fontFamily: 'var(--font-family-sans)', transition: 'all 0.15s', flexShrink: 0,
                  }}
                >
                  {filter}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* ── SECTION 1: KNOW BEFORE YOU ENROLL ────────────────────────── */}
      {riskCourses.length > 0 && (
        <div style={{ background: '#fff', borderBottom: '1px solid rgba(13,27,42,0.07)' }}>
          <div className="new-container" style={{ paddingTop: 48, paddingBottom: 48 }}>
            <div className="flex items-center gap-3" style={{ marginBottom: 8 }}>
              <div style={{ width: 4, height: 28, background: '#DC2626', borderRadius: 2, flexShrink: 0 }} />
              <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0D1B2A', margin: 0 }}>
                ⚠️ Know Before You Enroll
              </h2>
            </div>
            <p style={{ color: '#64748B', fontSize: 14, marginBottom: 28, paddingLeft: 16 }}>
              These courses have the highest stakes at UH — plan before you register
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
              {riskCourses.map(course => (
                <RiskCard key={course.key} course={course} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── SECTION 2: BROWSE BY COLLEGE ─────────────────────────────── */}
      <div ref={gridRef} className="new-container" style={{ paddingTop: 48, paddingBottom: 48 }}>
        <div className="flex items-start justify-between gap-4 flex-wrap" style={{ marginBottom: 8 }}>
          <div className="flex items-center gap-3">
            <div style={{ width: 4, height: 28, background: 'var(--school-primary)', borderRadius: 2, flexShrink: 0 }} />
            <div>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0D1B2A', margin: 0 }}>
                Browse by College
              </h2>
              {!isPending && collegeCategoryGroups.length > 0 && (
                <p style={{ fontSize: 13, color: '#64748B', margin: '4px 0 0' }}>
                  {collegeCategoryGroups.length} colleges and schools at UH
                </p>
              )}
            </div>
          </div>
        </div>

        {isPending ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16, marginTop: 28 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <CollegeCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16, marginTop: 28 }}>
              {displayedColleges.map(group => (
                <CollegeCard
                  key={group.identifier}
                  name={cleanGroupName(group.name)}
                  courseCount={group.courseCount}
                  href={group.href}
                  maxCourseCount={maxCourseCount}
                />
              ))}
              {filteredColleges.length === 0 && (
                <p style={{ color: '#94A3B8', gridColumn: '1 / -1', padding: '32px 0', textAlign: 'center' }}>
                  No colleges match "{activeFilter}".
                </p>
              )}
            </div>

            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: 24 }}>
                <button
                  onClick={() => setShowAllColleges(v => !v)}
                  style={{
                    padding: '10px 28px', borderRadius: 999,
                    border: '1.5px solid var(--school-primary)',
                    background: 'transparent', color: 'var(--school-primary)',
                    fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'var(--school-primary)'
                    e.currentTarget.style.color = '#fff'
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent'
                    e.currentTarget.style.color = 'var(--school-primary)'
                  }}
                >
                  {showAllColleges
                    ? 'Show less ↑'
                    : `Show all ${filteredColleges.length} colleges ↓`}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── SECTION 3: TRENDING COURSES ──────────────────────────────── */}
      {trendingCourses.length > 0 && (
        <div style={{ background: '#fff', borderTop: '1px solid rgba(13,27,42,0.07)', borderBottom: '1px solid rgba(13,27,42,0.07)' }}>
          <div className="new-container" style={{ paddingTop: 48, paddingBottom: 48 }}>
            <div className="flex items-center justify-between gap-4" style={{ marginBottom: 8 }}>
              <div className="flex items-center gap-3">
                <div style={{ width: 4, height: 28, background: 'var(--school-primary)', borderRadius: 2, flexShrink: 0 }} />
                <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0D1B2A', margin: 0 }}>
                  🔥 Most Searched Courses This Week
                </h2>
              </div>
              <a
                href="/search?q=COSC"
                style={{ fontSize: 13, color: 'var(--school-primary)', fontWeight: 600, whiteSpace: 'nowrap', textDecoration: 'none', flexShrink: 0 }}
              >
                View all →
              </a>
            </div>
            <p style={{ color: '#64748B', fontSize: 14, marginBottom: 24, paddingLeft: 16 }}>
              What UH students are looking up right now
            </p>
            <div
              style={{
                display: 'flex', gap: 16,
                overflowX: 'auto', scrollbarWidth: 'none',
                paddingBottom: 8,
              }}
            >
              {trendingCourses.map((course, i) => (
                <TrendingCourseCard
                  key={course.key}
                  course={course}
                  rank={i + 1}
                  enrichment={trendingEnrichments?.[decodeURIComponent(course.href.slice(3))]}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── SECTION 4: UDI SPOTLIGHT ──────────────────────────────────── */}
      <UDISpotlight courses={trendingCourses} />

      {/* ── SECTION 5: BOTTOM CTA ────────────────────────────────────── */}
      <div style={{
        background: 'var(--school-primary)',
        backgroundImage: 'linear-gradient(rgba(0,0,0,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.12) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
        padding: '72px 24px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'relative', zIndex: 1, maxWidth: 560, margin: '0 auto' }}>
          <h2 style={{ fontSize: 32, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', margin: '0 0 16px' }}>
            Your degree plan starts here.
          </h2>
          <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.8)', lineHeight: 1.65, marginBottom: 36 }}>
            Search any course, compare every professor, and know exactly what you're signing up for.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="/search?q="
              style={{
                padding: '14px 28px', borderRadius: 999,
                background: '#fff', color: 'var(--school-primary)',
                fontSize: 15, fontWeight: 700, textDecoration: 'none',
                fontFamily: 'var(--font-family-sans)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                transition: 'transform 0.15s, box-shadow 0.15s',
                display: 'inline-block',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)'
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)'
              }}
            >
              Search Courses →
            </a>
            <a
              href="/courses#browse"
              onClick={e => { e.preventDefault(); gridRef.current?.scrollIntoView({ behavior: 'smooth' }) }}
              style={{
                padding: '14px 28px', borderRadius: 999,
                background: 'transparent', color: '#fff',
                fontSize: 15, fontWeight: 600, textDecoration: 'none',
                fontFamily: 'var(--font-family-sans)',
                border: '1.5px solid rgba(255,255,255,0.5)',
                display: 'inline-block',
                transition: 'border-color 0.15s, background 0.15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.9)'
                e.currentTarget.style.background = 'rgba(255,255,255,0.1)'
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)'
                e.currentTarget.style.background = 'transparent'
              }}
            >
              Browse by Major →
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── RiskCard ─────────────────────────────────────────────────────────────────

type RiskCourse = SearchResult & { gpa: number | null; dropRate: number; udi: number | null }

function RiskCard({ course }: { course: RiskCourse }) {
  const code = course.title.split(': ')[0]
  const name = course.title.split(': ').slice(1).join(': ') || code
  const isHighRisk = course.udi !== null && course.udi >= 7.5
  const riskLabel = isHighRisk ? 'HIGH RISK' : 'PLAN CAREFULLY'
  const riskColor = isHighRisk ? '#DC2626' : '#D97706'
  const insight = syntheticInsight(course.gpa, course.dropRate)

  return (
    <a
      href={course.href}
      style={{
        display: 'block', textDecoration: 'none',
        background: '#fff', borderRadius: 16,
        border: '1px solid rgba(13,27,42,0.07)',
        boxShadow: '0 2px 8px rgba(13,27,42,0.05)',
        overflow: 'hidden',
        transition: 'transform 0.18s, box-shadow 0.18s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-3px)'
        e.currentTarget.style.boxShadow = '0 12px 32px rgba(13,27,42,0.12)'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(13,27,42,0.05)'
      }}
    >
      {/* Thin colored top bar */}
      <div style={{ height: 4, background: riskColor, width: '100%' }} />

      <div style={{ padding: '18px 20px 20px' }}>
        {/* Course code + risk badge */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{
            fontFamily: 'var(--font-family-mono)', fontSize: 12, fontWeight: 700,
            color: 'var(--school-primary)',
            background: 'rgba(var(--school-primary-rgb),0.08)',
            padding: '3px 10px', borderRadius: 6,
            border: '1px solid rgba(var(--school-primary-rgb),0.2)',
          }}>
            {code}
          </span>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
            color: riskColor, background: `${riskColor}18`,
            padding: '3px 8px', borderRadius: 6, textTransform: 'uppercase',
          }}>
            {riskLabel}
          </span>
        </div>

        {/* Course name */}
        <div style={{ fontSize: 15, fontWeight: 700, color: '#0D1B2A', marginBottom: 14, lineHeight: 1.35 }}>
          {name}
        </div>

        {/* GPA + UDI row */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 12, alignItems: 'center' }}>
          {course.gpa !== null && (
            <div>
              <div style={{
                fontFamily: 'var(--font-family-mono)', fontSize: 20, fontWeight: 700,
                color: course.gpa >= 2.5 ? '#059669' : '#DC2626', lineHeight: 1,
              }}>
                {course.gpa.toFixed(2)}
              </div>
              <div style={{ fontSize: 10, color: '#94A3B8', fontWeight: 600, marginTop: 2, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                Avg GPA
              </div>
            </div>
          )}
          {course.udi !== null && <UDIBadge udi={course.udi} />}
        </div>

        {/* Insight sentence */}
        <p style={{ fontSize: 13, color: '#64748B', fontStyle: 'italic', lineHeight: 1.5, marginBottom: 14, margin: '0 0 16px' }}>
          {insight}
        </p>

        {/* CTA button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <span style={{ fontSize: 13, color: 'var(--school-primary)', fontWeight: 700 }}>
            View Full Analysis →
          </span>
        </div>
      </div>
    </a>
  )
}

// ─── CollegeCard ─────────────────────────────────────────────────────────────

function CollegeCard({
  name, courseCount, href, maxCourseCount,
}: {
  name: string
  courseCount: number
  href: string
  maxCourseCount: number
}) {
  const emoji = getCollegeEmoji(name)
  const barPct = maxCourseCount > 0 ? Math.max(4, Math.round((courseCount / maxCourseCount) * 100)) : 4

  return (
    <a
      href={href}
      className="block no-underline"
      style={{
        background: '#fff', borderRadius: 16,
        padding: '20px 20px 18px 20px',
        border: '1px solid rgba(13,27,42,0.07)',
        borderLeft: '4px solid var(--school-primary)',
        boxShadow: '0 2px 8px rgba(13,27,42,0.04)',
        transition: 'all 0.18s', cursor: 'pointer', display: 'block',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-3px)'
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(13,27,42,0.10)'
        e.currentTarget.style.borderLeftWidth = '6px'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(13,27,42,0.04)'
        e.currentTarget.style.borderLeftWidth = '4px'
      }}
    >
      {/* Top: emoji + name */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 14 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12, flexShrink: 0,
          background: 'color-mix(in srgb, var(--school-primary) 10%, white)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22,
        }}>
          {emoji}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0D1B2A', margin: '0 0 3px', lineHeight: 1.3 }}>
            {name}
          </h3>
          <span style={{ fontSize: 12, color: '#64748B' }}>
            {courseCount > 0 ? `${courseCount.toLocaleString()} courses` : 'Courses available'}
          </span>
        </div>
      </div>

      {/* Middle: relative size bar */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ height: 5, background: 'rgba(13,27,42,0.07)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{
            height: '100%', width: `${barPct}%`,
            background: 'rgba(var(--school-primary-rgb),0.3)',
            borderRadius: 3, transition: 'width 0.6s ease',
          }} />
        </div>
      </div>

      {/* Bottom: explore link */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <span style={{ fontSize: 13, color: 'var(--school-primary)', fontWeight: 600 }}>
          Explore →
        </span>
      </div>
    </a>
  )
}

function CollegeCardSkeleton() {
  return (
    <div style={{
      background: '#fff', borderRadius: 16, padding: '20px',
      border: '1px solid rgba(13,27,42,0.07)', borderLeft: '4px solid #E2E8F0',
    }}>
      <div style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: '#F1F5F9', flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ height: 16, background: '#F1F5F9', borderRadius: 4, marginBottom: 6, width: '70%' }} />
          <div style={{ height: 12, background: '#F1F5F9', borderRadius: 4, width: '40%' }} />
        </div>
      </div>
      <div style={{ height: 5, background: '#F1F5F9', borderRadius: 3, marginBottom: 14 }} />
      <div style={{ height: 13, background: '#F1F5F9', borderRadius: 4, width: '25%', marginLeft: 'auto' }} />
    </div>
  )
}

// ─── TrendingCourseCard ───────────────────────────────────────────────────────

type TrendingCourse = SearchResult & { gpa: number | null; dropRate: number }

function TrendingCourseCard({ course, rank, enrichment }: { course: TrendingCourse; rank: number; enrichment?: CourseEnrichmentData | null }) {
  const code = course.title.split(': ')[0]
  const name = course.title.split(': ').slice(1).join(': ') || ''
  const udiScore = enrichment
    ? computeUDI(deriveUDIInputs({ avgGPA: enrichment.avgGPA, grades: enrichment.gradeCounts, sections: enrichment.sectionCount, schoolId: 'uh' })).udi
    : null

  return (
    <a
      href={course.href}
      style={{
        display: 'flex', flexDirection: 'column',
        width: 280, flexShrink: 0,
        background: '#fff', borderRadius: 14,
        border: '1px solid rgba(13,27,42,0.07)',
        borderLeft: '4px solid var(--school-primary)',
        boxShadow: '0 2px 8px rgba(13,27,42,0.05)',
        padding: '16px 18px',
        textDecoration: 'none',
        transition: 'transform 0.15s, box-shadow 0.15s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-3px)'
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(13,27,42,0.12)'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(13,27,42,0.05)'
      }}
    >
      {/* Rank + code */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 16, width: 24, flexShrink: 0, textAlign: 'center' }}>
          {rank === 1 ? '🔥' : `${rank}`}
        </span>
        <span style={{
          fontFamily: 'var(--font-family-mono)', fontSize: 11, fontWeight: 700,
          color: 'var(--school-primary)',
          background: 'rgba(var(--school-primary-rgb),0.08)',
          padding: '2px 8px', borderRadius: 5,
          border: '1px solid rgba(var(--school-primary-rgb),0.2)',
        }}>
          {code}
        </span>
      </div>

      {/* Name */}
      {name && (
        <div style={{
          fontSize: 13, fontWeight: 600, color: '#0D1B2A', lineHeight: 1.35,
          marginBottom: 12, flex: 1,
          overflow: 'hidden', display: '-webkit-box',
          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const,
        }}>
          {name}
        </div>
      )}

      {/* Stats row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {course.gpa !== null && (
            <span style={{
              fontSize: 12, fontWeight: 700,
              color: course.gpa >= 3.0 ? '#059669' : course.gpa >= 2.0 ? '#D97706' : '#DC2626',
            }}>
              {course.gpa.toFixed(2)} GPA
            </span>
          )}
          {udiScore !== null && <UDIBadge udi={udiScore} />}
        </div>
        <span style={{ fontSize: 13, color: 'var(--school-primary)', fontWeight: 700 }}>→</span>
      </div>
    </a>
  )
}

// ─── UDI Spotlight ────────────────────────────────────────────────────────────

const SIGNAL_ROWS: Array<{ key: keyof UDIResult['components']; label: string; weight: string }> = [
  { key: 'gpa', label: 'GPA Signal', weight: '30%' },
  { key: 'withdrawal', label: 'Withdrawal Rate', weight: '25%' },
  { key: 'fail', label: 'Fail Rate', weight: '20%' },
  { key: 'distribution', label: 'Grade Distribution', weight: '10%' },
]

function UDISpotlight({ courses }: { courses: TrendingCourse[] }) {
  const theme = getSchoolTheme('uh')
  const pills = courses.slice(0, 3)
  const [selectedIdx, setSelectedIdx] = useState(0)
  const [panelVisible, setPanelVisible] = useState(true)
  const [udiResult, setUdiResult] = useState<UDIResult | null>(null)
  const [isFetching, setIsFetching] = useState(false)

  const selected = pills[selectedIdx] ?? null

  // Fetch full grade distribution for selected course so we can use all UDI signals
  useEffect(() => {
    if (!selected) { setUdiResult(null); return }
    // Derive course name from href ("/c/CHEM%202323" → "CHEM 2323"), same as search.tsx line 134
    const courseId = decodeURIComponent(selected.href.slice(3))
    const controller = new AbortController()
    setIsFetching(true)
    fetch(`${API_ORIGIN.origin}/api/course/${encodeURIComponent(courseId)}`, { signal: controller.signal })
      .then(r => r.ok ? (r.json() as Promise<CourseSpotlightApiShape>) : Promise.reject(new Error(`${r.status}`)))
      .then(json => {
        const grades = { A: 0, B: 0, C: 0, D: 0, F: 0, W: 0 }
        for (const row of (json.dataGrid?.rows ?? [])) {
          grades.A += row.A ?? 0
          grades.B += row.B ?? 0
          grades.C += row.C ?? 0
          grades.D += row.D ?? 0
          grades.F += row.F ?? 0
          grades.W += row.W ?? 0
        }
        const total = grades.A + grades.B + grades.C + grades.D + grades.F + grades.W
        // Use badge GPA exactly as the course detail page does (parseGPAFromBadges)
        const avgGPA = parseFloat(json.badges?.find(b => b.key === 'gpa')?.text ?? '0') || 0
        const withdrawalRate = total > 0 ? (grades.W / total) * 100 : 0
        const failRate = total > 0 ? (grades.F / total) * 100 : 0
        setUdiResult(computeUDI(deriveUDIInputs({ avgGPA, grades, sections: json.sectionCount ?? 0, schoolId: 'uh' })))
      })
      .catch(err => {
        if ((err as Error).name === 'AbortError') return
        // Fallback to badges-only if the API call fails
        if (selected.gpa !== null) {
          setUdiResult(computeUDI(deriveUDIInputs({ avgGPA: selected.gpa, schoolId: 'uh' })))
        } else {
          setUdiResult(null)
        }
      })
      .finally(() => { if (!controller.signal.aborted) setIsFetching(false) })
    return () => controller.abort()
  }, [selected])

  const handleSelectCourse = (i: number) => {
    if (i === selectedIdx) return
    setPanelVisible(false)
    setUdiResult(null)
    setTimeout(() => { setSelectedIdx(i); setPanelVisible(true) }, 260)
  }

  // Nothing to show if no trending courses loaded yet
  if (pills.length === 0) return null

  const selectedCode = selected?.title.split(': ')[0] ?? ''
  const selectedName = selected?.title.split(': ').slice(1).join(': ') ?? ''

  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Gradient base */}
      <div
        aria-hidden
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: `linear-gradient(160deg, ${theme.primaryColor} 0%, color-mix(in srgb, ${theme.primaryColor} 65%, #000) 100%)`,
          zIndex: 0,
        }}
      />
      {/* Grid overlay */}
      <div
        aria-hidden
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage:
            'linear-gradient(rgba(0,0,0,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.12) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          zIndex: 1,
        }}
      />

      <div className="new-container" style={{ position: 'relative', zIndex: 2, padding: '72px 24px' }}>
        <div style={{ display: 'flex', gap: 48, alignItems: 'flex-start', flexWrap: 'wrap' }}>

          {/* Left: methodology copy */}
          <div style={{ flex: '0 0 52%', minWidth: 280 }}>
            <span style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.6)', display: 'block', marginBottom: 12,
            }}>
              What is UDI?
            </span>
            <h3 style={{
              fontSize: 'clamp(22px, 3vw, 30px)',
              fontWeight: 800, color: '#fff',
              letterSpacing: '-0.02em', lineHeight: 1.15,
              margin: '0 0 16px',
            }}>
              7 signals. One score.<br />No guesswork.
            </h3>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.75)', lineHeight: 1.7, marginBottom: 24 }}>
              The Unilytics Difficulty Index combines withdrawal rates, grade distributions,
              class size, professor variance, and semester trends into a single 0–10 score.
              Built on official university records — not opinions.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
              {[
                'Based on 133,000+ official grade records',
                'Updated every semester with new data',
                'Normalized across universities for fair comparison',
              ].map(bullet => (
                <div key={bullet} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={{ color: '#4ADE80', fontWeight: 700, fontSize: 14, flexShrink: 0, marginTop: 1 }}>✓</span>
                  <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>{bullet}</span>
                </div>
              ))}
            </div>
            <a
              href="/faq"
              style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.85)', textDecoration: 'none' }}
              onMouseEnter={e => { e.currentTarget.style.color = '#fff' }}
              onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.85)' }}
            >
              Learn about our methodology →
            </a>
          </div>

          {/* Right: live course UDI display */}
          <div style={{ flex: 1, minWidth: 260 }}>
            {/* Course selector pills */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
              {pills.map((c, i) => {
                const code = c.title.split(': ')[0]
                const isActive = i === selectedIdx
                return (
                  <button
                    key={c.key}
                    onClick={() => handleSelectCourse(i)}
                    style={{
                      padding: '6px 14px', borderRadius: 999, fontSize: 12, fontWeight: 700,
                      border: isActive ? '1.5px solid rgba(255,255,255,0.9)' : '1.5px solid rgba(255,255,255,0.25)',
                      background: isActive ? 'rgba(255,255,255,0.15)' : 'transparent',
                      color: isActive ? '#fff' : 'rgba(255,255,255,0.55)',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      fontFamily: 'var(--font-family-sans)',
                    }}
                  >
                    {code}
                  </button>
                )
              })}
            </div>

            {/* UDI panel */}
            <div
              className={`udi-fade-enter${panelVisible ? ' udi-fade-visible' : ''}`}
              style={{
                background: 'rgba(0,0,0,0.25)',
                borderRadius: 20,
                border: '1px solid rgba(255,255,255,0.1)',
                padding: '28px 28px 24px',
                backdropFilter: 'blur(8px)',
              }}
            >
              {/* Course header */}
              <div style={{ marginBottom: 20 }}>
                <div style={{
                  fontFamily: 'var(--font-family-mono)', fontSize: 13, fontWeight: 700,
                  color: 'rgba(255,255,255,0.9)', marginBottom: 4,
                }}>
                  {selectedCode}
                </div>
                {selectedName && (
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.4 }}>
                    {selectedName}
                  </div>
                )}
              </div>

              {/* Hero UDI badge */}
              {isFetching ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, minHeight: 80 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                    border: '3px solid rgba(255,255,255,0.15)',
                    borderTopColor: 'rgba(255,255,255,0.7)',
                    animation: 'spin 0.7s linear infinite',
                  }} />
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', fontStyle: 'italic' }}>
                    Computing score…
                  </span>
                </div>
              ) : udiResult ? (
                <UDIBadge
                  udi={udiResult.udi}
                  size="lg"
                  className="mb-6"
                />
              ) : (
                <div style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 64, fontWeight: 900, lineHeight: 1,
                  color: 'rgba(255,255,255,0.25)', letterSpacing: '-0.04em',
                  marginBottom: 24,
                }}>
                  —
                </div>
              )}

              {/* Signal breakdown */}
              {udiResult && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', marginBottom: 4 }}>
                    Signal Breakdown
                  </div>
                  {SIGNAL_ROWS.map(({ key, label, weight }) => {
                    const rawVal = udiResult.components[key]
                    const pct = Math.round((rawVal / 10) * 100)
                    return (
                      <div key={key}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
                            {label}
                            <span style={{ opacity: 0.45, marginLeft: 5 }}>{weight}</span>
                          </span>
                          <span style={{ fontSize: 11, fontFamily: 'var(--font-family-mono)', color: 'rgba(255,255,255,0.7)' }}>
                            {rawVal.toFixed(1)}
                          </span>
                        </div>
                        <div style={{ height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', width: `${pct}%`,
                            background: 'rgba(255,255,255,0.4)',
                            borderRadius: 2, transition: 'width 0.5s ease',
                          }} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}
